/**
 * The Measure tool's host (`projects/measurement-tool.md`, Phases 3 and 4): the rail-armed ruler,
 * the hold-M ruler, their Esc entries, the table's shared rulers, and the one `measureBetween` read
 * all of them draw from. Nothing is dispatched, so dismissing a ruler leaves no trace in game state
 * or undo.
 *
 * - Armed from the rail, Measure is a layer-3 armed thing (`armedSeam.ts`); a press on a cell or
 *   unit starts a ruler ({@link measurePress}). A drag's release, or a click's next click, dismisses
 *   it. Esc clears the ruler (layer 2), then disarms (layer 3).
 * - Hold-M is a layer-2 gesture, not an armed thing: it disarms nothing and the board's clicks pass
 *   through to whatever is armed. Keyup, window blur or Esc ends it.
 * - Shared live (ruling 2): the ruler showing here streams its ends to the room
 *   (`encounter_measure`), and every other participant's arrives as `remote`. Each client measures
 *   the ends against its own state, so a remote ruler reads what the local tool would. A remote end
 *   on a unit this viewer's state lacks (fog) draws nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type { Cell, EncounterState } from "@lancer-vtt/domain";
import { squareKey } from "@lancer-vtt/space";
import type { WireMeasureEnd } from "@lancer-vtt/shared";
import { asSquareCell } from "../combatCells";
import { measureBetween, type LoadedOverlay, type Measure } from "@lancer-vtt/rules";
import { useEscEntry, type EscLayers } from "../../../lib/encounter/escLayers";
import type { MeasureEvent } from "../../../lib/encounter/remoteEncounterController";
import type { MeasureRenderSpec } from "../pixiCombatBoard";
import { CURSOR_THROTTLE_MS } from "../combatStage/constants";
import { MEASURE_TOUCH_LINGER_MS, measureEndKey, measureMove, measurePress, measureReadout, measureRelease, type MeasureEnd, type MeasureHold, type MeasureRuler } from "./measureTool";

/** One ruler as `__combatBoardDebug.getMeasure()` reads it (cells as `x,y` keys). */
export type MeasureDebugRuler = { from: MeasureEnd; to: MeasureEnd; distance: number; range: number; sight: { visible: boolean; cover: string }; counted: string[] };

/** What `__combatBoardDebug.getMeasure()` reads: this viewer's ruler, and each other participant's by slot id. */
export type MeasureDebug = {
  local: MeasureDebugRuler | null;
  remote: (MeasureDebugRuler & { by: string })[];
};

/**
 * A board pointer event the stage reports: `move` on every pointer cell or unit change,
 * `press`/`release` only while armed. `touch`: the press came from a finger or pen
 * (`useMeasureTouch`). `dragged`: the release was past `measureTool.ts`'s `MEASURE_CLICK_SLOP_PX`
 * from the press, or off the board.
 */
export type MeasurePointer = (
  event: { phase: "press"; end: MeasureEnd; touch?: boolean } | { phase: "move"; end: MeasureEnd } | { phase: "release"; dragged: boolean },
) => void;

export type MeasureTool = {
  armed: boolean;
  setArmed: (armed: boolean) => void;
  onPointer: MeasurePointer;
  startHold: () => void;
  endHold: () => void;
  /** Every ruler to draw: the remote ones, then this viewer's on top. */
  render: MeasureRenderSpec[];
  debugRef: RefObject<MeasureDebug>;
};

type Ends = { from: MeasureEnd; to: MeasureEnd };
type SendMeasure = (measure: { from: WireMeasureEnd; to: WireMeasureEnd } | null) => void;

/**
 * Stream the showing ruler's ends to the room: a change of ends sends at most once per
 * {@link CURSOR_THROTTLE_MS}, the latest winning (a trailing send, so the final ends always go);
 * a clear goes at once, and unmounting clears.
 */
function useMeasureStream(send: SendMeasure | undefined, shown: Ends | null): void {
  const sendRef = useRef(send);
  sendRef.current = send;
  const lastSentAt = useRef(0);
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sentAny = useRef(false);
  const key = shown ? `${measureEndKey(shown.from)}|${measureEndKey(shown.to)}` : null;
  const shownRef = useRef(shown);
  shownRef.current = shown;
  useEffect(() => {
    const flush = () => {
      pending.current = null;
      lastSentAt.current = Date.now();
      const s = shownRef.current;
      sendRef.current?.(s ? { from: s.from, to: s.to } : null);
    };
    if (key === null) {
      if (pending.current) clearTimeout(pending.current);
      pending.current = null;
      if (sentAny.current) sendRef.current?.(null);
      sentAny.current = false;
      lastSentAt.current = 0;
      return;
    }
    sentAny.current = true;
    if (pending.current) return;
    const wait = CURSOR_THROTTLE_MS - (Date.now() - lastSentAt.current);
    if (wait <= 0) flush();
    else pending.current = setTimeout(flush, wait);
  }, [key]);
  useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current);
      if (sentAny.current) sendRef.current?.(null);
    },
    [],
  );
}

const cellKeys = (cells: Cell[]) => cells.map((c) => squareKey(asSquareCell(c)));

function renderSpec(m: Measure, ends: Ends, remote?: { by: string; name: string }): MeasureRenderSpec {
  const sq = (cells: Cell[]) => cells.map(asSquareCell);
  const footprints = [...("unit" in ends.from ? [m.fromCells] : []), ...("unit" in ends.to ? [m.toCells] : [])];
  return {
    nearest: [asSquareCell(m.nearest[0]), asSquareCell(m.nearest[1])],
    counted: sq(m.counted),
    footprints: footprints.map(sq),
    label: remote ? `${remote.name} · ${measureReadout(m)}` : measureReadout(m),
    ...(remote ? { by: remote.by } : {}),
  };
}

export function useMeasureTool(args: {
  esc: EscLayers;
  state: EncounterState | null;
  overlay: LoadedOverlay | undefined;
  /** The room's `sendMeasure`; absent, the ruler stays private. */
  send?: SendMeasure;
  /** The other participants' rulers (`useEncounter().measures`). */
  remote?: MeasureEvent[];
  /** A slot id's display name, for a remote ruler's readout. */
  nameOf?: (slot: string) => string;
}): MeasureTool {
  const { esc, state, overlay, send, remote = [], nameOf = (s: string) => s } = args;
  const [armed, setArmedRaw] = useState(false);
  const [ruler, setRuler] = useState<MeasureRuler | null>(null);
  const [hold, setHold] = useState<MeasureHold | null>(null);
  // The unit or cell under the pointer, for hold-M's start. A ref: it changes on every cell crossing
  // and nothing renders off it.
  const pointerEndRef = useRef<MeasureEnd | null>(null);

  const setArmed = useCallback((next: boolean) => {
    setArmedRaw(next);
    if (!next) setRuler(null);
  }, []);

  const onPointer = useCallback<MeasurePointer>((event) => {
    if (event.phase === "move") {
      const { end } = event;
      pointerEndRef.current = end;
      setRuler((r) => measureMove(r, end));
      setHold((h) => measureMove(h, end));
    } else if (event.phase === "press") {
      setRuler((r) => measurePress(r, event.end, event.touch));
    } else {
      setRuler((r) => measureRelease(r, event.dragged));
    }
  }, []);

  // A touch tap, tap ruler fades out on its own: the line never stays without a finger on it.
  useEffect(() => {
    if (ruler?.phase !== "fading") return;
    const timer = setTimeout(() => setRuler((r) => (r === ruler ? null : r)), MEASURE_TOUCH_LINGER_MS);
    return () => clearTimeout(timer);
  }, [ruler]);

  const startHold = useCallback(() => {
    const at = pointerEndRef.current;
    if (at) setHold({ from: at, to: at });
  }, []);
  const endHold = useCallback(() => setHold(null), []);

  // Layer 2: Esc ends the hold first, then clears the rail's ruler; the tool stays armed.
  useEscEntry(esc, {
    layer: "gesture",
    id: "measure-ruler",
    active: hold !== null || ruler !== null,
    onDismiss: () => (hold ? setHold(null) : setRuler(null)),
  });
  // Layer 3: the next Esc disarms the tool.
  useEscEntry(esc, { layer: "armed", id: "measure-tool", active: armed, onDismiss: () => setArmed(false) });

  // The hold-M ruler draws over the rail's while M is held.
  const shown = hold ?? ruler;
  useMeasureStream(send, shown);
  const measure = useMemo(() => (shown && state ? measureBetween({ state, from: shown.from, to: shown.to, overlay }) : null), [shown, state, overlay]);

  // `remote` is a fresh array on every room publish (a cursor move included), so key the fold on its content.
  const remoteKey = JSON.stringify(remote);
  const remoteMeasures = useMemo(
    () =>
      state
        ? remote.flatMap((r) => {
            const ends: Ends = { from: r.from as MeasureEnd, to: r.to as MeasureEnd };
            const m = measureBetween({ state, from: ends.from, to: ends.to, overlay });
            return m ? [{ by: r.by, ends, m }] : [];
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteKey, state, overlay],
  );

  const render = useMemo<MeasureRenderSpec[]>(
    () => [
      ...remoteMeasures.map(({ by, ends, m }) => renderSpec(m, ends, { by, name: nameOf(by) })),
      ...(measure && shown ? [renderSpec(measure, shown)] : []),
    ],
    // `nameOf` resolves names; a late name arrives with the next room publish.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteMeasures, measure, shown],
  );

  const debugRef = useRef<MeasureDebug>({ local: null, remote: [] });
  const debugOf = (m: Measure, ends: Ends): MeasureDebugRuler => ({ from: ends.from, to: ends.to, distance: m.distance, range: m.range, sight: m.sight, counted: cellKeys(m.counted) });
  debugRef.current = {
    local: measure && shown ? debugOf(measure, shown) : null,
    remote: remoteMeasures.map(({ by, ends, m }) => ({ by, ...debugOf(m, ends) })),
  };

  return { armed, setArmed, onPointer, startHold, endHold, render, debugRef };
}
